import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAssets } from '../../contexts/AssetContext';

const AssetLibrary = () => {
  const { t } = useTranslation();
  const { assets, addAsset, deleteAsset } = useAssets();
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    processFiles(files);
  };

  const processFiles = (files) => {
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        const newAsset = {
          name: file.name,
          type: file.type,
          size: file.size,
          content: reader.result, // This is a DataURL
          createdAt: new Date().toISOString(),
        };
        addAsset(newAsset);
      };
      reader.onerror = () => {
        console.error(`Failed to read file: ${file.name}`);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleDelete = (assetName) => {
    if (window.confirm(t('assetLibrary.confirmDelete', { name: assetName }))) {
      deleteAsset(assetName);
    }
  };

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  const handleUploadAreaClick = () => {
    fileInputRef.current.click();
  };

  return (
    <div className="bg-white rounded-xl p-6 mt-8">
      <h2 className="text-xl font-semibold mb-6">{t('assetLibrary.title')}</h2>

      <div
        className="border-2 border-dashed border-blue-500 rounded-xl p-8 text-center mb-6 bg-gray-50 cursor-pointer transition-colors duration-200 hover:bg-blue-50"
        onClick={handleUploadAreaClick}
      >
        <input
          type="file"
          multiple
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        <div className="text-3xl text-blue-500">📤</div>
        <p className="mt-2 text-gray-600">{t('assetLibrary.dropzone')}</p>
      </div>

      <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-6">
        {assets.map(asset => (
          <div key={asset.name} className="relative border border-gray-200 rounded-lg overflow-hidden transition-shadow duration-200 hover:shadow-lg group">
            <div className="w-full h-[120px] flex items-center justify-center bg-gray-50">
              {asset.type.startsWith('image/') ? (
                <img src={asset.content} alt={asset.name} className="w-full h-full object-cover" />
              ) : (
                <div className="text-5xl text-gray-400">📄</div>
              )}
            </div>
            <div className="p-3">
              <p className="m-0 text-sm font-medium text-gray-800 whitespace-nowrap overflow-hidden text-ellipsis" title={asset.name}>{asset.name}</p>
              <p className="text-xs text-gray-400 m-0">{formatBytes(asset.size)}</p>
            </div>
            <button
              className="absolute top-2 right-2 bg-black/60 text-white border-none rounded-full w-6 h-6 text-sm leading-6 text-center cursor-pointer opacity-0 transition-opacity duration-200 group-hover:opacity-100"
              onClick={() => handleDelete(asset.name)}
              title={t('assetLibrary.delete')}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AssetLibrary;
